import { inject, Injectable } from "@angular/core";
import { createEffect, ofType, Actions } from "@ngrx/effects";
import { map, catchError, of, switchMap, tap, mergeMap, EMPTY, delay, filter, from, finalize, bufferTime } from "rxjs";
import { CameraActions } from "./camera.actions";
import { CameraService } from "../../services/camera/camera.service";
import { SELECTED_SENSOR_KEY, getSnapshotKey } from "./camera.state";
import { CameraCacheService, SNAPSHOT_CACHE_MS } from "../../services/camera/camera-cache.service";
import { Store } from "@ngrx/store";
import { AppState } from "../root";
import { ToastService } from "../../services/toast/toast.service";
import { ToastType } from "../../models";
import { ISensor } from "../../models/sensor.interface";
import { RuleScheduleActions } from "../rule-schedule/rule-schedule.actions";


const CACHE_READ_BATCH_MS = 50;

@Injectable()
export class SensorEffects {
  private actions$ = inject(Actions);
  private store$ = inject(Store<AppState>);
  private cameraService = inject(CameraService);
  private toastService = inject(ToastService);
  private cameraCacheService = inject(CameraCacheService);

  loadCameras$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.loadCameras),
    switchMap(() => {
      return this.cameraService.getCameras().pipe(
        map(cameras => {
          const sortedCameras = [...cameras].sort((a, b) => {
            return a.name.localeCompare(b.name, undefined, { sensitivity: 'case' });
          });
          return CameraActions.loadCamerasSuccess({ sensors: sortedCameras });
        }),
        catchError(error => of(CameraActions.loadCamerasFailure({ error })))
      );
    })
  ));

  loadRules$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.loadRules),
    mergeMap((action) => {
      return this.cameraService.getRules(action.sensor).pipe(
        map(rules => {
          const sortedRules = [...rules].sort((a, b) => {
            return a.name.localeCompare(b.name, undefined, { sensitivity: 'case' });
          });
          return CameraActions.loadRulesSuccess({ sensor: action.sensor, rules: sortedRules });
        }),
        catchError(error => of(CameraActions.loadRulesFailure({ sensor: action.sensor, error })))
      );
    }, 10)
  ));

  loadAllRules$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.loadAllRules),
    switchMap(() => {
      return this.cameraService.getAllRules().pipe(
        map(rules => {
          const sortedRules = [...rules].sort((a, b) => {
            return a.name.localeCompare(b.name, undefined, { sensitivity: 'case' });
          });
          return CameraActions.loadAllRulesSuccess({ rules: sortedRules });
        }),
        catchError(error => of(CameraActions.loadAllRulesFailure({ error })))
      );
    })
  ));

  deleteRule$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.deleteRule),
    switchMap((action) => {
      return this.cameraService.deleteRule(action.rule).pipe(
        map(() => {
          this.toastService.showToast("Deleted rule successfully", ToastType.SUCCESS)
          return CameraActions.deleteRuleSuccess({ rule: action.rule })
        }),
        catchError(error => {
          this.toastService.showToast("Failed to delete rule", ToastType.ERROR)
          return of(CameraActions.deleteRuleFailure({ rule: action.rule, error }))
        })
      );
    })
  ));

  bulkDeleteRules$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.bulkDeleteRules),
    switchMap((action) => {
      return this.cameraService.bulkDeleteRules(action.ruleIds).pipe(
        map(ruleIds => {
          this.toastService.showToast(`Deleted ${ruleIds.length} rule(s) successfully`, ToastType.SUCCESS);
          return CameraActions.bulkDeleteRulesSuccess({ ruleIds });
        }),
        catchError(error => {
          this.toastService.showToast("Failed to delete rules", ToastType.ERROR);
          return of(CameraActions.bulkDeleteRulesError({ ruleIds: action.ruleIds, error }));
        })
      );
    })
  ));

  createRules$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.createRules),
    switchMap((action) => {
      return this.cameraService.createRules(action.rules).pipe(
        map(createdRule => {
          this.toastService.showToast("Created rules successfully", ToastType.SUCCESS);
          this.store$.dispatch(CameraActions.loadRules({ sensor: { id: action.cameraID } as ISensor }));
          const scheduleIds = action.linkedScheduleIds ?? [];
          const newRuleId = createdRule?.id;
          const hasNewRuleId = newRuleId != null && newRuleId.length > 0;
          if (scheduleIds.length > 0 && hasNewRuleId) {
            this.store$.dispatch(RuleScheduleActions.linkSchedules({ ruleIds: [newRuleId], scheduleIds }));
          }
          if (scheduleIds.length > 0 && !hasNewRuleId) {
            this.toastService.showToast("Rule created, but its schedules could not be linked", ToastType.ERROR);
          }
          return CameraActions.createRulesSuccess({ cameraID: action.cameraID });
        }),
        catchError(error => {
          this.toastService.showToast("Failed to create rules", ToastType.ERROR);
          return of(CameraActions.createRulesError({ sensorId: action.rules[0].camera_id, error }));
        })
      );
    })
  ));

  bulkCreateRules$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.bulkCreateRules),
    switchMap((action) => this.cameraService.bulkCreateRules(action.rules).pipe(
      map((createdRules) => {
        const ruleIds = (createdRules ?? []).map(rule => rule.id).filter((id): id is string => id != null);
        if (ruleIds.length !== action.rules.length) {
          this.toastService.showToast(`Created ${ruleIds.length} of ${action.rules.length} rule(s); some could not be linked to schedules`, ToastType.ERROR);
          return CameraActions.bulkCreateRulesSuccess({ ruleIds });
        }
        this.toastService.showToast(`Created ${action.rules.length} rule(s) successfully`, ToastType.SUCCESS);
        return CameraActions.bulkCreateRulesSuccess({ ruleIds });
      }),
      catchError(error => {
        this.toastService.showToast("Failed to bulk create rules", ToastType.ERROR);
        return of(CameraActions.bulkCreateRulesError({ error }));
      })
    ))
  ));

  updateRule$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.updateRule),
    switchMap((action) => {
      return this.cameraService.updateRule(action.rule).pipe(
        map(rule => {
          this.toastService.showToast("Updated rule successfully", ToastType.SUCCESS)
          return CameraActions.updateRuleSuccess({ rule: rule })
        }),
        catchError(error => {
          this.toastService.showToast("Failed to update rule", ToastType.ERROR)
          return of(CameraActions.updateRuleError({ rule: action.rule, error }))
        })
      );
    }))
  );

  bulkUpdateRules$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.bulkUpdateRules),
    switchMap((action) => {
      return this.cameraService.bulkUpdateRules(action.rules).pipe(
        map(rules => {
          this.toastService.showToast(`Updated ${action.rules.length} rule zone(s) successfully`, ToastType.SUCCESS)
          return CameraActions.bulkUpdateRulesSuccess({ rules })
        }),
        catchError(error => {
          this.toastService.showToast("Failed to update rule zones", ToastType.ERROR)
          return of(CameraActions.bulkUpdateRulesError({ rules: action.rules, error }))
        })
      );
    }))
  );

  // Deduplicates concurrent loads for the same snapshot key so overlapping requests
  // don't fire duplicate fetch/cache-write chains and race on the IndexedDB row.
  private loadingKeys = new Set<string>();

  loadSnapshot$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.loadSnapshot),
    filter((action) => {
      const key = getSnapshotKey(action.sensor.id, action.showHeatmap);
      if (this.loadingKeys.has(key)) {
        return false;
      }
      this.loadingKeys.add(key);
      return true;
    }),
    mergeMap((action) => {
      const key = getSnapshotKey(action.sensor.id, action.showHeatmap);
      return from(this.cameraCacheService.getEntry(key)).pipe(
        switchMap((entry) => {
          const isFresh =
            entry != null &&
            entry.imageBlob.size > 0 &&
            Date.now() - entry.cachedAt < SNAPSHOT_CACHE_MS;
          if (action.useCache && isFresh) {
            const url = this.cameraCacheService.createObjectURL(key, entry.imageBlob);
            return of(CameraActions.loadSnapshotSuccess({
              sensor: action.sensor,
              url,
              retrievalTime: new Date(entry.cachedAt),
              live: false,
              showHeatmap: action.showHeatmap
            }));
          }
          return this.fetchAndCacheSnapshot(action, key);
        }),
        // A broken IndexedDB read (quota, private browsing, etc.) must not kill this
        // effect stream — that would silently stop ALL future snapshot loads app-wide.
        // Fall back to a normal fetch instead of failing the cache lookup outright.
        catchError(() => this.fetchAndCacheSnapshot(action, key)),
        finalize(() => this.loadingKeys.delete(key))
      );
    })
  ));

  overrideSnapshot$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.overrideSnapshot),
    mergeMap((action) => {
      const key = getSnapshotKey(action.sensor.id, action.showHeatmap);
      return from(this.cacheAndCreateUrl(key, action.snapshot)).pipe(
        map((url) => CameraActions.loadSnapshotSuccess({
          sensor: action.sensor,
          url,
          retrievalTime: new Date(),
          live: false,
          showHeatmap: action.showHeatmap
        })),
        catchError((error) => of(CameraActions.loadSnapshotFailure({
          sensor: action.sensor,
          error,
          live: false,
          showHeatmap: action.showHeatmap
        })))
      );
    })
  ));

  // Strict persist: a failed write dispatches no setSnapshot, so existing snapshot
  // state stays untouched.
  primeSnapshotFromLivestream$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.primeSnapshotFromLivestream),
    mergeMap((action) => {
      const key = getSnapshotKey(action.sensorId, false);
      return from(this.cacheAndCreateUrl(key, action.base64, true)).pipe(
        map((url) => CameraActions.setSnapshot({
          sensorId: action.sensorId,
          url,
          retrievalTime: new Date()
        })),
        catchError((error) => {
          console.error('Failed to prime snapshot from livestream:', error);
          return of(null);
        }),
        filter((action): action is ReturnType<typeof CameraActions.setSnapshot> => action !== null)
      );
    })
  ));

  // Cards mount in bursts (a site page or virtual patrol opens 100+ at once), so the
  // window coalesces them into a single IndexedDB transaction.
  snapshotFromCache$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.loadSnapshotFromCache),
    bufferTime(CACHE_READ_BATCH_MS),
    filter((actions) => actions.length > 0),
    mergeMap((actions) => {
      const sensorIds = [...new Set(actions.map((action) => action.sensorId))];
      const keysBySensorId = new Map(sensorIds.map((sensorId) => [sensorId, getSnapshotKey(sensorId, false)]));
      return from(this.cameraCacheService.getEntries([...keysBySensorId.values()])).pipe(
        map((entries) => {
          const fresh: { sensorId: string, url: string, retrievalTime: Date }[] = [];
          for (const [sensorId, key] of keysBySensorId) {
            const entry = entries.get(key);
            if (entry == null || entry.imageBlob.size === 0 || Date.now() - entry.cachedAt >= SNAPSHOT_CACHE_MS) {
              continue;
            }
            fresh.push({
              sensorId,
              url: this.cameraCacheService.createObjectURL(key, entry.imageBlob),
              retrievalTime: new Date(entry.cachedAt)
            });
          }
          return fresh;
        }),
        map((fresh) => fresh.length > 0 ? CameraActions.setCachedSnapshots({ snapshots: fresh }) : null),
        catchError((error) => {
          console.error('Failed to read cached snapshots:', error);
          return of(null);
        }),
        filter((action): action is ReturnType<typeof CameraActions.setCachedSnapshots> => action !== null)
      );
    })
  ));

  private fetchAndCacheSnapshot(action: ReturnType<typeof CameraActions.loadSnapshot>, key: string) {
    return this.cameraService.getSnapshot(action.sensor, action.showHeatmap ?? false).pipe(
      switchMap((base64) => from(this.cacheAndCreateUrl(key, base64))),
      map((url) => CameraActions.loadSnapshotSuccess({
        sensor: action.sensor,
        url,
        retrievalTime: new Date(),
        live: true,
        showHeatmap: action.showHeatmap
      })),
      // Only a failed fetch reaches here — a failed cache write is handled in
      // cacheAndCreateUrl and does not misreport the retrieval as a failure.
      catchError((error) => of(CameraActions.loadSnapshotFailure({
        sensor: action.sensor,
        error,
        live: true,
        showHeatmap: action.showHeatmap
      })))
    );
  }

  // Persists the image and returns a renderable URL. A cache-write failure is logged
  // but non-fatal unless strict: priming relies solely on the cached blob surviving
  // the session, so there a failed write must propagate.
  private async cacheAndCreateUrl(key: string, base64: string | null | undefined, strict = false): Promise<string> {
    const trimmed = (base64 ?? '').trim();
    if (!trimmed) {
      throw new Error('Snapshot image is empty');
    }
    const blob = await this.cameraCacheService.base64ToBlob(trimmed);
    if (blob.size === 0) {
      throw new Error('Snapshot image is empty');
    }
    if (strict) {
      await this.cameraCacheService.putBlob(key, blob);
      return this.cameraCacheService.createObjectURL(key, blob);
    }
    try {
      await this.cameraCacheService.putBlob(key, blob);
    } catch (error) {
      console.error('Failed to persist snapshot to cache:', error);
    }
    return this.cameraCacheService.createObjectURL(key, blob);
  }

  loadMutedSensors$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.loadMutedSensors),
    switchMap((action) => {
      return this.cameraService.getMuted(action.sensors).pipe(
        map(mutedSensors => {
          return CameraActions.loadMutedSensorsSuccess({mutedSensors: mutedSensors})
        }),
        catchError(error => of(CameraActions.loadMutedSensorsFailure({ error: error })))
      );
    })
  ))

  muteSensors$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.muteSensors),
    mergeMap((action) => {
      return this.cameraService.mute(action.sensors, action.duration, action.reason).pipe(
        map(() => {
          this.toastService.showToast("Muted cameras succcessfully", ToastType.SUCCESS)
          const ids: string[] = action.sensors.map(sensor => sensor.id)
          const mute_reason: string = action.reason
          const mute_timestamp: string = new Date().toISOString()
          const mute_expiry_timestamp: string = new Date(new Date().getTime() + action.duration * 1000).toISOString()
          return CameraActions.muteSensorsSuccess(
            {
              ids: ids,
              muteReason: mute_reason,
              muteTimestamp: mute_timestamp,
              muteExpiryTimestamp: mute_expiry_timestamp
            }
          )
        }),
        catchError(error => {
          this.toastService.showToast("Failed to mute cameras", ToastType.ERROR)
          return of(CameraActions.muteSensorsFailure(error))
        })
      );
    })
  ));

  muteBySimilarity$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.muteBySimilarity),
    mergeMap((action) => {
      return this.cameraService.muteSimilar({ alertId: action.alertId, detectionId: action.detectionId, similarity: action.similarity, muteForSeconds: action.muteForSeconds, comment: action.comment }).pipe(
        map(() => {
          this.toastService.showToast("Similarity mute created", ToastType.SUCCESS);
          return CameraActions.muteBySimilaritySuccess();
        }),
        catchError(error => {
          this.toastService.showToast("Failed to create similarity mute", ToastType.ERROR);
          return of(CameraActions.muteBySimilarityFailure({ error }));
        })
      );
    })
  ));

  unmuteSensors$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.unmuteSensors),
    mergeMap((action) => {
      return this.cameraService.unmute(action.sensors).pipe(
        map(() => {
          this.toastService.showToast("Unmuted cameras succcessfully", ToastType.SUCCESS)
          return CameraActions.unmuteSensorsSuccess({ ids: action.sensors.map(sensor => sensor.id) })
        }),
        catchError(error => {
          this.toastService.showToast("Failed to unmute cameras", ToastType.ERROR)
          return of(CameraActions.unmuteSensorsFailure(error))
        })
      );
    })
  ));

  // region Storage
  // Logic for storing information so that it is available on reloads

  storeCurrentSensor$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.setSelectedSensor),
    tap((action) => {
      sessionStorage.setItem(SELECTED_SENSOR_KEY, JSON.stringify(action.sensor))
    })
  ), { dispatch: false }
  );

  //endregion

  loadSchedules$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.loadSchedules),
    mergeMap((action) => {
      return this.cameraService.getSchedules(action.sensor).pipe(
        map(schedules => CameraActions.loadSchedulesSuccess({ sensor: action.sensor, schedules: schedules })),
        catchError(error => of(CameraActions.loadRulesFailure({ sensor: action.sensor, error: error })))
      );
    })
  ));

  createSchedules$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.createSchedule),
    switchMap((action) => {
      return this.cameraService.createSchedule(action.schedule, action.sensor).pipe(
        map(schedule => {
          this.toastService.showToast("Created schedule successfully", ToastType.SUCCESS)
          return CameraActions.createScheduleSuccess({ schedule: schedule, sensor: action.sensor })
        }),
        catchError(error => {
          this.toastService.showToast("Failed to create schedule", ToastType.ERROR)
          return of(CameraActions.createScheduleError({ sensorId: action.sensor.id, error }))
        })
      );
    })
  ));

  updateSchedules$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.updateSchedule),
    switchMap((action) => {
      return this.cameraService.updateSchedule(action.schedule, action.sensor).pipe(
        map(schedule => {
          this.toastService.showToast("Updated recording schedule successfully", ToastType.SUCCESS)
          return CameraActions.updateScheduleSuccess({ schedule: schedule, sensor: action.sensor })
        }),
        catchError(error => {
          this.toastService.showToast("Failed to update recording schedule", ToastType.ERROR)
          return of(CameraActions.updateScheduleError({ sensorId: action.sensor.id, error }))
        })
      );
    }))
  );

  deleteSchedules$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.deleteSchedule),
    switchMap((action) => {
      return this.cameraService.deleteSchedule(action.schedule).pipe(
        map(() => {
          this.toastService.showToast("Deleted schedule successfully", ToastType.SUCCESS)
          return CameraActions.deleteScheduleSuccess({ schedule: action.schedule, sensor: action.sensor })
        }),
        catchError(error => {
          this.toastService.showToast("Failed to delete schedule", ToastType.ERROR)
          return of(CameraActions.deleteScheduleError({ schedule: action.schedule, sensor: action.sensor, error }))
        })
      );
    })
  ));

  startLivestream$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.startCameraLivestream),
    mergeMap((action) => {
      return this.cameraService.startLivestream(action.sensorIDs).pipe(
        catchError(error => {
          console.error(error);
          return EMPTY;
        })
      );
    })
  ), { allowSignalWrites: true, dispatch: false });

  resetLivestream$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.resetCameraLivestream),
    mergeMap((action) => {
      return this.cameraService.removeLivestream(action.sensorIDs).pipe(
        delay(2000),
        map(() => CameraActions.startCameraLivestream({ sensorIDs: action.sensorIDs })),
        catchError(error => {
          console.error(error);
          return EMPTY;
        })
      );
    })
  ));

  applyQuickMask$ = createEffect(() => this.actions$.pipe(
    ofType(CameraActions.applyQuickMask),
    mergeMap((action) => {
      return this.cameraService.applyQuickMask(action.quickMasks).pipe(
        catchError(error => {
          console.error(error);
          this.toastService.showToast("Failed to apply quick mask", ToastType.ERROR)
          return EMPTY;
        })
      );
    })
  ), { allowSignalWrites: true, dispatch: false });

}
